/* eslint-disable no-promise-executor-return, consistent-return */
import { createCoreAxiosInstance } from 'createAxiosInstance';

import * as Sentry from '@sentry/react';

import {
  POS,
  SKIP_THE_DISHES,
  CHOWNOW,
  DOORDASH,
  SUBMIT_ACTIONS,
  INTEGRATION_STATUS,
  SQUARE,
  CLOVER,
  REFERRAL_PARTNERS,
  UBEREATS,
  LIGHTSPEED,
  ONBOARDING_STATUS_MAP,
  CN_POS_STATUS,
  SQUARE_LEGACY_POS_KEY,
  SQUARE_LEGACY_POS_LOGO_KEY,
  SQUARE_LEGACY_POS_NAME,
  TOAST_POS_KEY,
} from 'constants/integrations';
import { POS_COMPANIES } from 'constants/chownow';
import { REQUEST_TYPE } from 'constants/api';

import { axiosApiRequest } from 'helpers/api';

export const checkIntegrationIsActive = integration =>
  integration?.activeData?.active || integration?.activeData?.status === 'active';

export async function getPOSIntegrations(companyID, setError) {
  const data = await axiosApiRequest({
    method: REQUEST_TYPE.GET,
    path: `/api/v2/pos-integrations?companyID=${companyID}`,
    setError,
  });
  const addMeta = Object.keys(data.pointOfSales ?? {}).map(pos => ({
    pos,
    ...data.pointOfSales[pos],
  }));
  return addMeta;
}

/**
 *
 * @param {number} merchantID
 * @returns {Array} An array of apps and external integrations from core,
 * formatted as objects. Objects contain the app name as app (String), the
 * meta data (Object), and any available active data the integration has (Object)
 *
 */
export async function getPlatformIntegrationsV2(merchantID, setError) {
  const data = await axiosApiRequest({
    method: REQUEST_TYPE.GET,
    path: `/api/v2/app-integrations?merchantID=${merchantID}`,
    setError,
  });
  const addMeta = Object.keys(data.applications).map(app => ({
    app,
    ...data.applications[app],
  }));
  return addMeta;
}

export const formatPosIntegrations = pos =>
  pos?.map(system => ({
    key: system.pos,
    name: system.metaData?.name,
    submitAction: system.metaData?.submitAction ?? SUBMIT_ACTIONS.SUBMIT,
    fields: system.metaData?.fields ?? [],
    excerpt: system.metaData?.excerpt ?? 'Point of Sale System',
    status: system.isActive ? INTEGRATION_STATUS.ACTIVE : INTEGRATION_STATUS.INACTIVE,
    imageUrl: system.metaData?.imageUrl,
    isExternalIntegration: system.metaData?.is_external_integration ?? false,
    integrationType: 'pos',
    activeData: system.activeData && {
      ...system.activeData,
      external_location_id:
        system.activeData?.external_location_id ||
        system.activeData?.store_id ||
        system.activeData?.merchant_id ||
        system.activeData?.location_id,
    },
    connectionUrl: system.metaData?.one_dash_connection_url,
    extraInfo: {
      bypassConnectionPage: true,
      ...system.metaData,
    },
    resourceName: system.metaData?.resourceName,
    template: system.metaData?.template,
    isSquare: system.pos.toUpperCase().includes(SQUARE),
    isClover: system.pos.toUpperCase().includes(CLOVER),
    isLightspeed: system.pos.toUpperCase().includes(LIGHTSPEED),
  }));

/** Active beats authorized: a paused integration is still connected, just not sending orders. */
function getChowNowPosStatus(integration) {
  if (integration.is_active) {
    return CN_POS_STATUS.ACTIVE;
  }

  return integration.is_authorized ? CN_POS_STATUS.PAUSED : CN_POS_STATUS.AVAILABLE;
}

/**
 * Shapes a ChowNow-native POS integration into what IntegrationsView.Card expects, since Cuboh
 * returns neither Toast nor legacy Square in its `pointOfSales` payload.
 */
function formatChowNowPosCard(integration, { detailsPath, key, logoKey, name }) {
  if (!integration) {
    return null;
  }

  return {
    detailsPath,
    excerpt: 'Point of Sale System',
    // IntegrationsForm reduces over `fields` unguarded
    fields: [],
    integrationType: POS.toLowerCase(),
    // must stay false — these enable the drawer submit, which navigates to an undefined
    // connectionUrl
    isClover: false,
    isLightspeed: false,
    isSquare: false,
    key,
    ...(logoKey && { logoKey }),
    name,
    status: getChowNowPosStatus(integration),
  };
}

export function formatToastPosIntegration(toastIntegration, { detailsPath }) {
  return formatChowNowPosCard(toastIntegration, {
    detailsPath,
    key: TOAST_POS_KEY,
    name: POS_COMPANIES.toast.name,
  });
}

/**
 * The legacy (ChowNow-core) Square integration, which survives until those locations migrate to
 * Cuboh-Square. Callers pass it only for an already-live connection.
 */
export function formatSquareLegacyPosIntegration(squareIntegration, { detailsPath }) {
  return formatChowNowPosCard(squareIntegration, {
    detailsPath,
    key: SQUARE_LEGACY_POS_KEY,
    logoKey: SQUARE_LEGACY_POS_LOGO_KEY,
    name: SQUARE_LEGACY_POS_NAME,
  });
}

/**
 * Merges the ChowNow-native POS cards into the Cuboh POS list. Nothing here can duplicate a
 * Cuboh entry: a Toast location has no Cuboh POS, and Cuboh's own `square` keeps its own card.
 *
 * A native card carrying `logoKey` shares a brand with a Cuboh entry (square-legacy/square), so
 * it slots in beside that entry and the two Squares read as a pair. Everything else appends.
 */
export function mergeChowNowPosIntegrations(integrations, chowNowPosCards = []) {
  const cards = chowNowPosCards.filter(Boolean);

  if (!integrations || !cards.length) {
    return integrations;
  }

  const merged = [...(integrations[POS] ?? [])];

  cards.forEach(card => {
    const twinIndex = card.logoKey ? merged.findIndex(({ key }) => key === card.logoKey) : -1;

    if (twinIndex === -1) {
      merged.push(card);
    } else {
      merged.splice(twinIndex + 1, 0, card);
    }
  });

  return { ...integrations, [POS]: merged };
}

/** A ChowNow-native card owns its own page. Cuboh-supplied entries never carry one. */
export const isChowNowPosIntegration = integration => !!integration?.detailsPath;

/**
 * True when any entry on the page came from Cuboh. Gates the Cuboh support copy, which is wrong
 * for a location whose only integrations are ChowNow-native.
 */
export const hasCubohIntegrations = integrations =>
  !!integrations &&
  Object.values(integrations).some(
    group => Array.isArray(group) && group.some(entry => !isChowNowPosIntegration(entry)),
  );

/**
 * The shape formatIntegrations returns, empty. Seeds a location with no Cuboh account, where the
 * Cuboh fetch never runs. Module-level and frozen so its identity is stable across renders.
 */
export const EMPTY_INTEGRATIONS = Object.freeze({
  platforms: [],
  [POS]: [],
  [REFERRAL_PARTNERS]: [],
});

export function formatIntegrations(platforms, pos) {
  // We can pass in new integration types as necessary i.e. POS
  const statusEnum = Object.freeze({ pending: 1, active: 2, inactive: 3 });

  const formattedReferrals = [];

  // filter out test integrations
  const formattedPlatforms = platforms
    .filter(({ app }) => !(app.includes('test') || app.includes('dev') || app === 'cuboh'))
    .map(platform => {
      const bypassConnectionPage = ![SKIP_THE_DISHES, CHOWNOW, DOORDASH].includes(
        platform.app.toUpperCase(),
      );

      if (platform.metaData?.referral_info) {
        formattedReferrals.push(platform.metaData?.referral_info);
      }

      return {
        key: platform.app,
        name: platform.metaData?.name,
        submitAction: platform.metaData?.submitAction ?? SUBMIT_ACTIONS.SUBMIT,
        fields: platform.metaData?.fields ?? [],
        excerpt: platform.metaData?.excerpt ?? 'This is a delivery platform',
        status: platform.activeData?.status ?? INTEGRATION_STATUS.INACTIVE,
        imageUrl: platform.metaData?.imageUrl,
        isExternalIntegration: platform.metaData?.is_external_integration ?? false,
        integrationType: platform.metaData?.integration_type,
        activeData: platform.activeData && {
          ...platform.activeData,
          external_location_id:
            platform.activeData?.external_location_id ||
            platform.activeData?.store_id ||
            platform.activeData?.merchant_id ||
            platform.activeData?.location_id,
        },
        connectionUrl: platform.metaData?.one_dash_connection_url,
        extraInfo: {
          bypassConnectionPage,
          ...platform.metaData,
        },
      };
    })
    .sort(({ key: aKey, status: aStatus }, { key: bKey, status: bStatus }) => {
      if (statusEnum[aStatus] > statusEnum[bStatus]) return 1;
      if (statusEnum[aStatus] < statusEnum[bStatus]) return -1;
      if (aKey > bKey) return 1;
      if (aKey < bKey) return -1;
      return 0;
    });

  const formattedPOS = formatPosIntegrations(pos);

  return {
    platforms: formattedPlatforms,
    [POS]: formattedPOS,
    [REFERRAL_PARTNERS]: formattedReferrals,
  };
}

/**
 * Uses values to decide whether to update or create using V2 onboarding
 *
 * @param {object} values - Values used on the backend to create/update an integration
 * @param {boolean} hasActiveData - Whether or not the integration has active data
 *
 * @returns result of request to the backend
 *
 */
async function createIntegration(values) {
  const axiosInstance = await createCoreAxiosInstance();
  const { data } = await axiosInstance.post('/api/v2/app-integrations/', values);
  return data;
}

async function updateIntegration(values) {
  const axiosInstance = await createCoreAxiosInstance();
  const { data } = await axiosInstance.patch('/api/v2/app-integrations/update/', values);
  return data;
}

export async function createOrUpdateIntegration({ hasActiveData, values }) {
  // if the app has no V1 onboarder or the app has been flagged for official onboarding,
  // use the V2 onboarder/updater
  return hasActiveData ? updateIntegration(values) : createIntegration(values);
}

export async function verifyStoreId(values) {
  const axiosInstance = await createCoreAxiosInstance();
  const { data } = await axiosInstance.post('/api/v2/app-integrations/store-info/', values);
  return data.data;
}

/**
 * opens the uber merchant log out window, then closes it
 * @returns true if all went well, false if error caught
 */
async function logoutFromUber() {
  try {
    const uberLogoutWindow = window.open(
      'https://auth.uber.com/login/logout?next_url=https://auth.uber.com/login',
      '_blank',
      'popup,width=200,height=200,',
    );

    return await new Promise(resolve =>
      setTimeout(() => {
        uberLogoutWindow.close();
        return resolve(true);
      }, 5000),
    );
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

/**
 * opens the chownow merchant log out window, then closes it
 * @returns true if all went well, false if error caught
 */
async function logoutFromChownow() {
  try {
    const chownowLogoutWindow = window.open(
      'https://integrations.chownowapi.com/accounts/logout/',
      '_blank',
      'popup,width=200,height=200,',
    );
    return await new Promise(resolve =>
      setTimeout(() => {
        chownowLogoutWindow.close();
        return resolve(true);
      }, 5000),
    );
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

async function logoutFromDoordash() {
  try {
    const doordashLogoutWindow = window.open(
      'https://www.doordash.com/merchant/logout',
      '_blank',
      'popup,width=200,height=200,',
    );
    return await new Promise(resolve =>
      setTimeout(() => {
        doordashLogoutWindow.close();
        return resolve(true);
      }, 5000),
    );
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

/**
 * switch on the integration name and select/invoke the function to log out of the integration
 * @param {string} app - app name from core
 * @returns true if logout was successful, false if not or integration not found
 */

async function logoutFromIntegration(app) {
  switch (app) {
    case UBEREATS.toLowerCase():
      return logoutFromUber();
    case CHOWNOW.toLowerCase():
      return logoutFromChownow();
    case DOORDASH.toLowerCase():
      return logoutFromDoordash();
    case LIGHTSPEED.toLowerCase():
      return true;
    default:
      return false;
  }
}

/**
 * Function to log out from an OAuth official partner and then
 * navigate to their official OAuth connection endpoint
 *
 * @param {string} app - Name of the application from the BE
 *
 * @param {string} authenticationLink - Link to the partner OAuth "Connect" endpoint
 *
 */

export async function authenticateIntegration(app, authenticationLink) {
  try {
    const canAuthenticate = await logoutFromIntegration(app);
    if (canAuthenticate) {
      window.location.href = authenticationLink;
    } else {
      throw new Error('Cannot properly authenticate. Unable to verify login status');
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function formatConnectionUrl({ connectionUrl, companyId, merchantId }) {
  return connectionUrl
    .toString()
    .replaceAll('cuboh_merchant_id', merchantId)
    .replaceAll('cuboh_company_id', companyId);
}

/*
 * Send a request to hit the Activate endpoint for the given app for the merchant integration
 * of the given merchantID
 */
export async function sendActivateIntegrationRequest(merchantID, app) {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { data } = await axiosInstance.post(`/api/v2/app-integrations/${app}-activation/`, {
      merchant_id: merchantID,
    });
    return data;
  } catch (e) {
    return {
      status: 'failed',
      message:
        'Unable to activate due to server error. Please try again in a few moments or contact support for further assistance.',
    };
  }
}

export async function getNetNewOnboardUrl(merchantID, app) {
  const axiosInstance = await createCoreAxiosInstance();
  const { data } = await axiosInstance.get(`/api/v2/app-integrations/${app}-net-new/${merchantID}`);
  return data;
}

export const getSquareMerchantLocations = async companyID => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { data } = await axiosInstance.get(`api/v2/pos/square/locations/${companyID}`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
  }
};

export async function getLightSpeedBusinesses(companyID, setError) {
  const data = await axiosApiRequest({
    method: REQUEST_TYPE.GET,
    path: `api/v2/pos/lightspeed/businesses/${companyID}`,
    setError,
  });
  return data;
}

export const getPosResourceByCompanyID = async companyID => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const {
      data: { resource },
    } = await axiosInstance.get(`api/v2/pos-resource/${companyID}`);
    return resource;
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const updatePosResourceByCompanyID = async (companyID, values) => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { status } = await axiosInstance.post(`api/v2/pos-resource/${companyID}`, values);
    return status;
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const getStatusForApp = integrationToUse => {
  if (
    integrationToUse.key.toUpperCase() === DOORDASH &&
    integrationToUse.status === INTEGRATION_STATUS.PENDING
  ) {
    return integrationToUse.activeData?.onboarding_process_status
      ? `${integrationToUse.status} - ${
          ONBOARDING_STATUS_MAP[DOORDASH][integrationToUse.activeData?.onboarding_process_status]
        }`
      : INTEGRATION_STATUS.INACTIVE;

    // append the status of the onboarding process to the integration status
  }
  return integrationToUse.status;
};
